import {generateTranslatedActivityTypeString} from "../../../utils/string/common";
import {ACTIVITY_TYPE, AllActivityTypes} from "../../../types-enums/activity";
import {i18n} from "i18next";

/**
 * Generates a string explaining why a droppable action is disabled for a specific activity
 * based on its type, position in the journey, and the journey's total duration.
 *
 * @param {AllActivityTypes} activity - The activity object containing information about the activity type and other attributes.
 * @param {number | null | undefined} day - The day in the journey where the activity occurs, can be null or undefined if not applicable.
 * @param {i18n} i18n - Internationalization instance used for translating messages based on the application's current locale.
 * @param {number | undefined} journeyDurationInDays - The total duration of the journey in days, used to validate specific activity rules.
 * @returns {string} Translated string detailing the reason why the droppable action is disabled, or null for unsupported activity types.
 */
export const generateWhyDroppableDisabledString = (
    activity: AllActivityTypes,
    day: number | null | undefined,
    i18n: i18n,
    journeyDurationInDays: number | undefined,
) => {
    const {t} = i18n
    const {common: {activityType}} = activity
    const commonMessage = t("HINTS.JOURNEY.DROPPABLE_DISABLED_MOVING_GROUP", {
        activity: generateTranslatedActivityTypeString(activityType, i18n),
        interpolation: {escapeValue: false}
    })
    switch (activityType) {
        case ACTIVITY_TYPE.car_bus:
            return commonMessage
        case ACTIVITY_TYPE.flight:
            return null
        case ACTIVITY_TYPE.hotel:
            if (day === journeyDurationInDays) return "HINTS.JOURNEY.DROPPABLE_DISABLED_HOTEL_JOURNEY_LAST_DAY"
            return commonMessage
        default:
            activityType satisfies  never;
    }
}


/**
 * Determines whether a droppable action is disabled on the last day of a journey
 * based on the activity type and the current day.
 *
 * @param {AllActivityTypes | null} activity - The activity object, containing details such as activity type. Can be null if no activity exists.
 * @param {number | null | undefined} day - The current day of the journey. Can be null or undefined if not provided.
 * @param {number | undefined} journeyDurationInDays - The total duration of the journey in days. Can be undefined if not available.
 * @returns {boolean} - Returns `true` if droppable actions should be disabled for the given conditions,
 *                      otherwise returns `false`.
 */
export const disableDroppableOnLastDayOfJourney = (
    activity: AllActivityTypes | null,
    day: number | null | undefined,
    journeyDurationInDays: number | undefined,
): boolean => {
    if (activity) {
        const {common: {activityType}} = activity
        switch (activityType) {
            case ACTIVITY_TYPE.car_bus:
                return false
            case ACTIVITY_TYPE.flight:
                return false
            case ACTIVITY_TYPE.hotel:
                if (day === journeyDurationInDays) return true
                return false
            default:
                activityType satisfies  never;
        }
    }
    return false
}
